import { useEffect, useRef } from 'react';

interface ScrollEventHandlerParams {
  updateTooltipState: (x: number, y: number) => void;
  throttleDelay: number;
  lastPositionRef: React.MutableRefObject<{ x: number; y: number } | null>;
}

export const useScrollEvents = ({
  updateTooltipState,
  throttleDelay,
  lastPositionRef,
}: ScrollEventHandlerParams) => {
  const rafRef = useRef<number | null>(null);
  const throttleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isThrottledRef = useRef(false);

  useEffect(() => {
    const handleScroll = () => {
      if (!lastPositionRef.current || isThrottledRef.current) return;

      isThrottledRef.current = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);

      rafRef.current = requestAnimationFrame(() => {
        if (lastPositionRef.current) {
          updateTooltipState(
            lastPositionRef.current.x,
            lastPositionRef.current.y,
          );
        }
      });

      throttleTimerRef.current = setTimeout(() => {
        isThrottledRef.current = false;
      }, throttleDelay);
    };

    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (throttleTimerRef.current) clearTimeout(throttleTimerRef.current);
      isThrottledRef.current = false;
    };
  }, [updateTooltipState, throttleDelay, lastPositionRef]);
};
